import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrash } from 'react-icons/fa';
import ConfirmationModal from '../components/ConfirmationModal';
import PopupMessage from '../components/PopupMessage';

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

const CustomerListPage = ({ onLogout }) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
    const [forceRefetch, setForceRefetch] = useState(false);
    const [popup, setPopup] = useState({ message: '', type: '' });
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        isOpen: false,
        customerId: null,
        customerName: '',
    });

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const styles = {
        mainContainer: {
            maxWidth: '1400px',
            margin: '2rem auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: 'white'
        },
        container: {
            padding: '2rem',
            fontFamily: 'IRANSans, sans-serif',
            direction: 'rtl',
            backgroundImage: 'linear-gradient(270deg, rgb(53, 53, 53), rgb(104, 123, 146))',
            color: 'white',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            color: 'white',
        },
        createButtonLink: { backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '5px', cursor: 'pointer', fontSize: '1rem', textDecoration: 'none' },
        searchInput: { width: '100%', padding: '0.75rem', fontSize: '1rem', marginBottom: '1rem', boxSizing: 'border-box', borderRadius: '5px', border: '1px solid #888' },
        table: { width: '100%', borderCollapse: 'collapse', color: 'white' },
        th: {
            cursor: 'pointer',
            padding: '0.75rem',
            borderBottom: '2px solid rgba(255, 255, 255, 0.3)',
            textAlign: 'right',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        td: {
            padding: '0.75rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            textAlign: 'right',
            verticalAlign: 'middle',
        },
        pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' },
        button: { padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '1rem', borderRadius: '5px', border: '1px solid #ddd' },
        actionsCell: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        editIcon: {
            color: 'white',
            fontSize: '1.2rem',
            textDecoration: 'none'
        },
        deleteIcon: { color: '#ff6b6b', fontSize: '1.2rem', cursor: 'pointer' },
    };

    useEffect(() => {
        const fetchCustomers = async () => {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                onLogout();
                return;
            }
            try {
                const params = new URLSearchParams({ page, limit: 15, search: debouncedSearchTerm, sort: sortConfig.key, dir: sortConfig.direction });
                const response = await fetch(`https://interactivebot.tgnsys.ir/api/get_customers.php?${params.toString()}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.status === 401) {
                    onLogout();
                    return;
                }
                const result = await response.json();
                if (result.status === 'success') {
                    setCustomers(result.data);
                    setTotalPages(result.pagination.totalPages);
                } else {
                    console.error("Failed to fetch customers:", result.message);
                }
            } catch (error) {
                console.error("API Error:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCustomers();
    }, [page, debouncedSearchTerm, sortConfig, onLogout, forceRefetch]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setPage(1);
        setSortConfig({ key, direction });
    };

    const SortArrow = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) return null;
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    const handleDeleteClick = (customerId, customerName) => {
        setDeleteConfirmation({ isOpen: true, customerId, customerName });
    };

    const confirmDelete = async () => {
        const { customerId } = deleteConfirmation;
        const token = localStorage.getItem('token');
        if (!token) {
            onLogout();
            return;
        }
        try {
            const response = await fetch('https://interactivebot.tgnsys.ir/api/delete_customer.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ id: customerId }),
            });
            const result = await response.json();
            if (response.ok) {
                setPopup({ message: result.message, type: 'success' });
                setForceRefetch(prev => !prev);
            } else {
                setPopup({ message: `خطا در حذف: ${result.message}`, type: 'error' });
            }
        } catch (error) {
            setPopup({ message: `خطا در ارتباط با سرور: ${error.message}`, type: 'error' });
        } finally {
            setDeleteConfirmation({ isOpen: false, customerId: null, customerName: '' });
        }
    };

    return (
        <div style={styles.mainContainer}>
            <PopupMessage message={popup.message} type={popup.type} onClose={() => setPopup({ message: '', type: '' })} />
            <div style={styles.container}>
                <div style={styles.header}>
                    <h2>لیست مشتریان</h2>
                    <Link to="/create" style={styles.createButtonLink}>
                        ایجاد مشتری جدید
                    </Link>
                </div>
                <input
                    type="text"
                    placeholder="جستجو در نام، شماره تلفن و شماره سرویس..."
                    style={styles.searchInput}
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setPage(1);
                    }}
                />
                {loading ? <p>در حال بارگذاری...</p> : (
                    <>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, cursor: 'default' }}>ردیف</th>
                                    <th style={styles.th} onClick={() => handleSort('full_name')}>نام کامل<SortArrow columnKey="full_name" /></th>
                                    <th style={styles.th} onClick={() => handleSort('phone_number')}>شماره تلفن<SortArrow columnKey="phone_number" /></th>
                                    <th style={styles.th} onClick={() => handleSort('service_number')}>شماره سرویس<SortArrow columnKey="service_number" /></th>
                                    <th style={styles.th} onClick={() => handleSort('due_date')}>تاریخ سررسید<SortArrow columnKey="due_date" /></th>
                                    <th style={styles.th} onClick={() => handleSort('max_daily_messages')}>حداکثر پیام<SortArrow columnKey="max_daily_messages" /></th>
                                    <th style={styles.th} onClick={() => handleSort('created_at')}>تاریخ ایجاد<SortArrow columnKey="created_at" /></th>
                                    <th style={styles.th}>چت آی‌دی تلگرام</th>
                                    <th style={{ ...styles.th, cursor: 'default' }}>عملیات</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.map((customer, index) => (
                                    <tr key={customer.id}>
                                        <td style={styles.td}>{((page - 1) * 15) + index + 1}</td>
                                        <td style={styles.td}>{customer.full_name}</td>
                                        <td style={styles.td}>{customer.phone_number}</td>
                                        <td style={styles.td}>{customer.service_number}</td>
                                        <td style={styles.td}>{customer.due_date ? new Date(customer.due_date).toLocaleDateString('fa-IR') : '--'}</td>
                                        <td style={styles.td}>{customer.max_daily_messages}</td>
                                        <td style={styles.td}>{new Date(customer.created_at).toLocaleDateString('fa-IR')}</td>
                                        <td style={styles.td}>{customer.telegram_chat_id}</td>
                                        <td style={{ ...styles.td, ...styles.actionsCell }}>
                                            <Link to={`/edit/${customer.id}`} style={styles.editIcon}>
                                                <FaEdit />
                                            </Link>
                                            <FaTrash 
                                                style={styles.deleteIcon}
                                                onClick={() => handleDeleteClick(customer.id, customer.full_name)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={styles.pagination}>
                            <button style={styles.button} onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}>
                                قبلی
                            </button>
                            <span>صفحه {page} از {totalPages > 0 ? totalPages : 1}</span>
                            <button style={styles.button} onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page >= totalPages}>
                                بعدی
                            </button>
                        </div>
                    </>
                )}
            </div>
            <ConfirmationModal
                isOpen={deleteConfirmation.isOpen}
                onClose={() => setDeleteConfirmation({ isOpen: false, customerId: null, customerName: '' })}
                onConfirm={confirmDelete}
                title="تایید حذف مشتری"
                message={`آیا از حذف مشتری "${deleteConfirmation.customerName}" مطمئن هستید؟ این عمل غیرقابل بازگشت است.`}
            />
        </div>
    );
};

export default CustomerListPage;